import * as React from 'react';

import {
  Button,
  Col,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Menu,
  Row,
} from '@rematter/ui-web';
import { formatDistanceToNow } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheck,
  LucideCircleCheck,
  LucideEye,
  LucideMoreHorizontal,
  LucidePanelLeft,
  LucidePencil,
  LucideShare2,
  LucideTrash2,
  LucideTriangleAlert,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { useAssistantChatEventsSubscription } from '../../_graphql/subscriptionAssistnatChatEvents';
import * as conversationState from '../../_state/chatConversation.state';
import * as inputState from '../../_state/chatInput.state';
import * as chatListState from '../../_state/chatList.state';
import { $isSharedChatView } from '../../_state/sharedChat.state';
import { ChatMessageRecord } from '../../_types/messageParts';
import { useTranslations } from '../../i18n/useTranslations';

import { MessageInput } from './MessageInput';
import { MessageList } from './MessageList';
import { PromptList } from './PromptList';
import { ShareDialog } from './ShareDialog';

interface ChatConversationContentProps {
  selectedChatId: string | null;
  isLoadingChatMessages: boolean;
  messages: ChatMessageRecord[];
  isSharedView: boolean;
  showLoadedBanner: boolean;
  onSelectPrompt: (prompt: string) => void;
}

//
// ⚛️ ⚛️ ⚛️ CHAT CONVERSATION CONTENT ⚛️ ⚛️ ⚛️
//
const ChatConversationContent = ({
  selectedChatId,
  isLoadingChatMessages,
  messages,
  isSharedView,
  showLoadedBanner,
  onSelectPrompt,
}: ChatConversationContentProps) => {
  const t = useTranslations();

  if (!selectedChatId) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-neutral/60">
        {t.sidebar.selectChat}
      </div>
    );
  }

  return (
    <div className="@ChatConversationContent flex h-full overflow-y-auto">
      <AnimatePresence mode="wait">
        {isLoadingChatMessages && (
          <motion.div
            key={`loading-${selectedChatId}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="flex flex-1 items-center justify-center"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral/20 border-t-primary" />
          </motion.div>
        )}

        {!isLoadingChatMessages && messages.length === 0 && (
          <motion.div
            key={`prompt-list-${selectedChatId}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex flex-1 flex-col items-center overflow-hidden pt-[12%]"
          >
            <PromptList onSelectPrompt={onSelectPrompt} />
            <div className="w-full max-w-4xl">
              <MessageInput autoFocus />
            </div>
          </motion.div>
        )}

        {!isLoadingChatMessages && messages.length > 0 && (
          <motion.div
            key={`message-list-${selectedChatId}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="relative flex flex-1 flex-col overflow-hidden"
          >
            <AnimatePresence>
              {showLoadedBanner && (
                <motion.div
                  key="loaded-banner"
                  initial={{ opacity: 0, y: -40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -40 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="absolute inset-x-0 top-0 z-10 flex justify-center px-4 pt-3"
                >
                  <Row
                    gap2
                    className="rounded-lg border border-success/30 bg-white px-4 py-2.5 shadow-md"
                  >
                    <LucideCircleCheck className="h-4.5 w-4.5 text-success" />
                    <span className="text-sm font-medium text-success">
                      {t.chat.loadedPreviousChat}
                    </span>
                  </Row>
                </motion.div>
              )}
            </AnimatePresence>
            <MessageList messages={messages} />
            {isSharedView && (
              <Row
                gap2
                xCenter
                className="border-t border-neutral/20 px-4 py-3 text-sm text-neutral/60"
              >
                <LucideEye className="size-4" />
                {t.chat.readOnlyMessage}
              </Row>
            )}
            {!isSharedView && <MessageInput />}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface ChatConversationProps {
  onOpenList: () => void;
}

//
// ⚛️ ⚛️ ⚛️ CHAT CONVERSATION ⚛️ ⚛️ ⚛️
//
export const ChatConversation = ({ onOpenList }: ChatConversationProps) => {
  // Translations
  const t = useTranslations();

  // Refs
  const renameInputRef = React.useRef<HTMLInputElement>(null);
  const wasLoadingRef = React.useRef(false);

  // States
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = React.useState(false);
  const [isRenaming, setIsRenaming] = React.useState(false);
  const [renameValue, setRenameValue] = React.useState('');
  const [showLoadedBanner, setShowLoadedBanner] = React.useState(false);

  // Atoms
  const selectedChatId = useAtomValue(conversationState.$selectedChatId);
  const messages = useAtomValue(conversationState.$allMessages);
  const isLoadingChatMessages = useAtomValue(
    conversationState.$isLoadingChatMessages,
  );
  const isSharedView = useAtomValue($isSharedChatView);
  const { data: chatListData } = useAtomValue(
    chatListState.$chatsWithOptimisticTitles,
  );
  const setMessageInput = useSetAtom(inputState.$messageInput);
  const archiveChat = useSetAtom(conversationState.$archiveChat);
  const renameChat = useSetAtom(conversationState.$renameChat);
  const setSubscriptionStatus = useSetAtom(
    conversationState.$subscriptionStatus,
  );
  const handleSubscriptionEvent = useSetAtom(
    conversationState.$handleSubscriptionEvent,
  );
  const loadMessagesFromServer = useSetAtom(
    conversationState.$loadMessagesFromServer,
  );
  const clearLocalState = useSetAtom(conversationState.$clearLocalState);

  // Data hooks
  const subscriptionResult = useAssistantChatEventsSubscription(
    {
      chatId: selectedChatId ?? '',
    },
    {
      skip: !selectedChatId,
      onEvent: handleSubscriptionEvent,
    },
  );

  // Memoized values
  const selectedChat = React.useMemo(
    () => chatListData?.chats.find((c) => c.id === selectedChatId),
    [chatListData, selectedChatId],
  );

  const menuItems = React.useMemo(
    () => [
      {
        label: t.header.share,
        icon: <LucideShare2 />,
        onAction: () => setIsShareDialogOpen(true),
      },
      {
        label: t.header.rename,
        icon: <LucidePencil />,
        onAction: () => {
          setRenameValue(selectedChat?.title || '');
          setIsRenaming(true);
        },
      },
      {
        label: t.header.delete,
        icon: <LucideTrash2 />,
        className: 'text-failure',
        onAction: () => {
          setIsDeleteDialogOpen(true);
        },
      },
    ],
    [t, selectedChat],
  );

  // Callbacks
  const handleSelectPrompt = React.useCallback(
    (prompt: string) => {
      setMessageInput(prompt);
    },
    [setMessageInput],
  );

  const handleRenameSubmit = React.useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== selectedChat?.title) {
      renameChat({ title: trimmed });
    }
    setIsRenaming(false);
  }, [renameValue, selectedChat?.title, renameChat]);

  const handleRenameKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleRenameSubmit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsRenaming(false);
      }
    },
    [handleRenameSubmit],
  );

  const handleConfirmDelete = React.useCallback(async () => {
    if (!selectedChatId) return;
    setIsDeleting(true);
    try {
      await archiveChat();
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedChatId, archiveChat]);

  // Effects
  /** Clears local state and loads messages when the selected chat changes */
  React.useEffect(() => {
    if (selectedChatId) {
      clearLocalState();
      loadMessagesFromServer();
    }
  }, [selectedChatId, clearLocalState, loadMessagesFromServer]);

  /** Tracks subscription status and triggers reconnection on errors */
  React.useEffect(() => {
    if (!selectedChatId) {
      setSubscriptionStatus('disconnected');
      return;
    }

    if (subscriptionResult.error) {
      setSubscriptionStatus('reconnecting');

      setTimeout(() => {
        loadMessagesFromServer();
        setSubscriptionStatus('connected');
      }, 2000);
    } else if (subscriptionResult.data) {
      setSubscriptionStatus('connected');
    }
  }, [
    selectedChatId,
    subscriptionResult.error,
    subscriptionResult.data,
    setSubscriptionStatus,
    loadMessagesFromServer,
  ]);

  /** Shows a "loaded" banner after messages finish loading */
  React.useEffect(() => {
    if (isLoadingChatMessages) {
      wasLoadingRef.current = true;
    } else if (wasLoadingRef.current) {
      const hadMessages = messages.length > 0;
      wasLoadingRef.current = false;
      if (hadMessages) {
        setShowLoadedBanner(true);
        const timer = setTimeout(() => setShowLoadedBanner(false), 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoadingChatMessages, messages.length]);

  /** Resets the loaded banner when switching to a different chat */
  React.useEffect(() => {
    setShowLoadedBanner(false);
  }, [selectedChatId]);

  /** Focuses and selects the rename input when entering rename mode */
  React.useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  return (
    <Col className="@ChatConversation h-full">
      <Row gap3 className="border-b border-neutral/20 px-6 pt-6 pb-3">
        <Button
          size="icon"
          variant="outline"
          onPress={onOpenList}
          icon={<LucidePanelLeft />}
        />
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={renameInputRef}
              className="w-full rounded border border-neutral/30 bg-transparent px-1 text-base font-semibold outline-none focus:border-primary"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={handleRenameSubmit}
            />
          ) : (
            <Row gap2 className="truncate text-base font-semibold">
              <span className="truncate">
                {selectedChat?.title || t.sidebar.newChat}
              </span>
              {selectedChat?.isShared && (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                  <LucideShare2 className="size-3" />
                  {t.header.shared}
                </span>
              )}
            </Row>
          )}
          {selectedChat && (
            <div className="truncate text-sm text-neutral/60">
              {t.header.lastUpdated}{' '}
              {formatDistanceToNow(selectedChat.updatedAt.toDate(), {
                addSuffix: true,
              })}
            </div>
          )}
        </div>
        {selectedChatId && (
          <>
            {isRenaming && (
              <span onMouseDown={(e) => e.preventDefault()}>
                <Button
                  size="icon"
                  variant="outline"
                  icon={<LucideCheck />}
                  onPress={handleRenameSubmit}
                />
              </span>
            )}
            <Menu
              trigger={
                <Button
                  size="icon"
                  variant="outline"
                  icon={<LucideMoreHorizontal />}
                />
              }
              items={menuItems}
            />
          </>
        )}
      </Row>

      <ChatConversationContent
        selectedChatId={selectedChatId}
        isLoadingChatMessages={isLoadingChatMessages}
        messages={messages}
        isSharedView={isSharedView}
        showLoadedBanner={showLoadedBanner}
        onSelectPrompt={handleSelectPrompt}
      />

      {selectedChatId && (
        <ShareDialog
          chatId={selectedChatId}
          isOpen={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
        />
      )}

      <DialogOverlay
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <DialogContent position="center">
          <DialogHeader>
            <DialogTitle>
              <Row gap2 className="text-xl font-semibold text-failure">
                <LucideTriangleAlert className="size-5" />
                {t.header.deleteDialog.title}
              </Row>
            </DialogTitle>
          </DialogHeader>
          <div className="p-4">
            <p className="text-sm text-neutral/70">
              {t.header.deleteDialog.message}
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => setIsDeleteDialogOpen(false)}
              isDisabled={isDeleting}
            >
              {t.header.deleteDialog.cancel}
            </Button>
            <Button
              variant="solid"
              onPress={handleConfirmDelete}
              isDisabled={isDeleting}
              isLoading={isDeleting}
              className="bg-failure text-white hover:bg-failure/90"
            >
              {t.header.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogOverlay>
    </Col>
  );
};
